import { ActionType, nanoid, ProColumns } from "@ant-design/pro-components";
import { Button, Checkbox, CheckboxProps, DatePicker, Form, Input, InputRef, Select, Space, Tooltip } from "antd";
import { CheckStatus, CloseStatus, Filter, Print, Trash } from "base/ui/components/Icons";
import CustomProTable, { tablePagination } from "base/ui/components/CustomTable";
import { DATE_FORMAT_DEFAULT, PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from "utils/constants";
import { SearchBox, SearchForm, SearchItem } from "pages/Components/SearchBox";
import { checkPermissionWithId, useCheckPermission } from "utils/permissionHelper";
import { cloneDeep, omit, reverse, sortBy, uniq } from "lodash";
import { commonPresenter, commonStore, wasteHandoverPlanPresenter, wasteHandoverPlanStore } from "stores/root_store";
import { getFullName, getRole, getRoles, getUserName } from "utils/localStorageHelper";
import { saveSearchParams, getSearchParams } from "utils/searchParamsHelper";
import { useCallback, useEffect, useRef, useState } from "react";
import BoxContainer from "base/ui/components/BoxContainer";
import { CommonModel } from "services/Main/common_services";
import CustomButton from 'base/ui/components/Button';
import CustomButtonIcon from "base/ui/components/Button/buttonIcon";
import { WasteHandoverRegistrationModel } from "models/WasteHandoverRegistration";
import { FaFilter } from "react-icons/fa";
import { FilterDropdownProps } from "antd/es/table/interface";
import { HiSearch } from "react-icons/hi";
import { Link } from "react-router-dom";
import PageWrapper from "pages/Components/PageWrapper"
import { ROUTES } from "base/routes/routes";
import SaveReportForm from "pages/Components/reportForm";
import { WasteHandoverRegistrationSearchPayload } from "services/Main/wasteHandoverPlan_services";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { debounce } from "utils/debounce";
import moment from "moment";
import { observer } from "mobx-react"
import { removeEmptyValues } from "utils/transformHelper";
import { useResizableColumns } from "utils/reportHelper";
import { ExportButton } from "pages/Components/FunctionButton";
import { useTranslation } from "react-i18next";
import CancelConfirm from "./CancelConfirm";


// Phiếu đăng ký bàn giao chất thải / phế liệu
// màn hình phiếu đăng ký bàn giao chất thải/ phế liệu
const initialSearch: WasteHandoverRegistrationSearchPayload = {
  paging: {
    pageSize: PAGE_SIZE_DEFAULT,
    pageIndex: 1,
    orderBy: undefined,
    orderByDesc: undefined,
  },
  documentDateFrom: dayjs().format('YYYY-MM-DD'),
  documentDateTo: dayjs().format('YYYY-MM-DD'),
  ...(getRole() === "NCC" && { vendorNumber: getUserName() }),
}

// màn hình phiếu đăng ký bàn giao chất thải/ phế liệu
export function createWasteHandoverRegistrationList() {
  return observer(() => {
    const { t } = useTranslation();
    document.title = t('waste-handover-plan-register.title') + ' - ' + SYSTEM_TITLE;
    // phân quyền
    useCheckPermission("INDEX")
    const rolesList = (() => {
        const rolesRaw = getRoles();
        return Array.isArray(rolesRaw)
            ? rolesRaw
            : (typeof rolesRaw === 'string' ? rolesRaw.split(',').map((r: string) => r.trim()) : []);
    })();
    const isHuyPhieuChatThai = rolesList.includes('HUYPHIEUCHATTHAI');
    const isCancel = checkPermissionWithId("CANCEL") || isHuyPhieuChatThai;
    // disable nhập ngày khi chọn khác "Custom"
    const [disableDatePicker, setDisableDatePicker] = useState(false)
    const [searchForm] = Form.useForm()
    // mở drawer search
    const [openDrawer, setOpenDrawer] = useState(false)

    //=== COLUMNS TABLE =================================
    const [dataPaging, setDataPaging] = useState<WasteHandoverRegistrationSearchPayload>(initialSearch);
    // data search danh sách đăng ký bàn giao chất thải
    const [dataTable, setDataTable] = useState<WasteHandoverRegistrationModel[]>([]);
    const [listVendor, setListVendor] = useState<CommonModel[]>([])
    const [tableLoading, setTableLoading] = useState(true)
    // mở modal cancel
    const [modalCancelOpen, setModalCancelOpen] = useState(false)
    // data cancel
    const [dataCancel, setDataCancel] = useState<WasteHandoverRegistrationModel>()
    const [cancelLoading, setCancelLoading] = useState(false)

    const searchInput = useRef<InputRef>(null);
    const [searchText, setSearchText] = useState<Record<string, string>>({});
    const [searchedColumn, setSearchedColumn] = useState('');
    const [tableKey, setTableKey] = useState(0)

    // Lấy roles từ localStorage
    const roles = getRoles() || [];

    const handleSearch = (
      _selectedKeys: string[],
      confirm: FilterDropdownProps['confirm'],
      dataIndex: keyof WasteHandoverRegistrationModel,
    ) => {
      confirm();
      setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void, confirm: FilterDropdownProps['confirm'], dataIndex: keyof WasteHandoverRegistrationModel) => {
      clearFilters();
      confirm()
      setSearchText(prev => ({ ...prev, [dataIndex]: '' }))
    };

    const getColumnSearchProps = (dataIndex: keyof WasteHandoverRegistrationModel, type?: 'arrayString' | 'arrayDate' | 'date'): ProColumns<WasteHandoverRegistrationModel> => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Tìm kiếm`}
            onChange={(e) => setSearchText(prev => ({ ...prev, [dataIndex]: e.target.value }))}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <hr className="mb-2 mt-1" />
          {(() => {
            let arrString = [] as string[];
            switch (type) {
              case 'arrayString':
                arrString = dataTable.flatMap(data => data[dataIndex] ? data[dataIndex] : ['-']) as string[];
                break;
              case 'arrayDate':
                const arrDate = dataTable.flatMap(data => data[dataIndex] ? data[dataIndex] : ['-']) as string[];
                const sorted = sortBy(arrDate, (date) => new Date(date).getTime());
                arrString = reverse(sorted.map(data => data !== '-' ? dayjs(data).format('DD/MM/YYYY') : data))
                break;
              case "date":
                const arrDates = dataTable.map(data => data[dataIndex] ? dayjs(data[dataIndex] as string).format('DD/MM/YYYY') : '-') as string[];
                arrString = arrDates
                break;
              default:
                arrString = dataTable.map(data => data[dataIndex] ? data[dataIndex] : '-') as any[];
                break;
            }

            // loại bỏ các mã trùng
            const uniqueArr = uniq(arrString);
            const searchArr = uniqueArr.filter(i => searchText ? (i + '').toLowerCase().includes(searchText[dataIndex]?.toLowerCase() || '') : true)
            const checkedAll = searchArr.length === selectedKeys.length
            const indeterminate = selectedKeys.length > 0 && selectedKeys.length < searchArr.length;
            const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
              setSelectedKeys(e.target.checked ? searchArr as string[] : []);
            };

            return (
              <Space direction='vertical' className="max-h-[250px] overflow-y-auto w-full" >
                <Checkbox checked={checkedAll}
                  onChange={onCheckAllChange} indeterminate={indeterminate}>Tất cả</Checkbox>
                <Checkbox.Group value={selectedKeys as string[]} onChange={(checkValues) => {
                  setSelectedKeys(checkValues as string[])
                }}>
                  <Space direction='vertical'>
                    {
                      searchArr.map((item, index) => (
                        <Checkbox key={nanoid()} value={item}>
                          {item}
                        </Checkbox>
                      ))
                    }
                  </Space>
                </Checkbox.Group>
              </Space>
            )
          })()}

          <hr className="mb-2 mt-1" />
          <Space>
            <Button
              type="primary"
              htmlType="button"
              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters, confirm, dataIndex)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <FaFilter style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (type === 'arrayString') {
          return ((record[dataIndex] || []) as string[]).some(v => v.toString().toLowerCase() === (value as string).toLowerCase())
        }
        if (type === 'arrayDate') {
          return ((record[dataIndex] || []) as string[]).some(v => dayjs(v).format("DD/MM/YYYY") === (value as string))
        }
        if (type === 'date') {
          return (record[dataIndex] ? dayjs(record[dataIndex] as string).format("DD/MM/YYYY") : '-').includes(value as string)
        }

        return (record[dataIndex] || '-')
          .toString()
          .toLowerCase() === (value as string).toLowerCase()

      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    });

    const defaultDataTable: ProColumns<WasteHandoverRegistrationModel>[] = [
      {
        title: t('waste-handover-plan-register.print'),
        dataIndex: 'print',
        key: 'print',
        width: 70,
        fixed: 'left',
        align: 'center',
        render(text, record) {
          return <div className="flex w-full justify-center items-center group cursor-pointer">
            <Link to={`${ROUTES.WASTE_HANDOVER_REGISTRATION_PRINT.BASE.replace('/*', '')}${ROUTES.WASTE_HANDOVER_REGISTRATION_PRINT.CONTENT.replace(':wasteHandoverRegistrationId', record.wasteHandoverRegistrationId)}`} target="_blank">
              <Print className="fill-blue-800" width="13" />
            </Link>
          </div>
        }
      },
      {
        title: t('masterdata.product.stt'),
        dataIndex: 'stt',
        key: 'stt',
        fixed: 'left',
        width: 50,
        align: 'center',
      },
      {
        title: t('waste-handover-plan-register.code'),
        dataIndex: 'wasteHandoverRegistrationCode',
        key: 'wasteHandoverRegistrationCode',
        fixed: 'left',
        width: 150,
        render(text, record) {
          return <div className="flex gap-2 items-center group cursor-pointer">
            <Link to={`${ROUTES.WASTE_HANDOVER_REGISTRATION.EDIT_REGISTRATION.LINK}/${record.wasteHandoverRegistrationId}`} className="group-hover:text-blue-600">{text}</Link>
          </div>
        },
        ...getColumnSearchProps('wasteHandoverRegistrationCode')
      },
      {
        title: t('masterdata.vendor.vendor-code'),
        dataIndex: 'vendorNumber',
        key: 'vendorNumber',
        width: 150,
        fixed: 'left',
        ...getColumnSearchProps('vendorNumber')
      },
      {
        title: t('waste-handover-plan.vendor-name-search'),
        dataIndex: 'vendorName',
        key: 'vendorName',
        width: 500,
        ...getColumnSearchProps('vendorName')
      },
      {
        title: t('waste-handover-plan-register.status'),
        dataIndex: 'taskStatusName',
        width: 140,
        key: 'taskStatusName',
        render: (text, record) => {
          return <div className="w-full !text-[10px] !text-center font-medium !py-0.5 border rounded-sm"
            style={{
              borderColor: record.color + '80',
              color: record.color,
              backgroundColor: record.color + '1a'
            }}
          >{text}</div>
        },
        ...getColumnSearchProps('taskStatusName')
      },
      {
        title: t('waste-handover-plan.delivery-schedule-time'),
        dataIndex: 'deliveredTimes',
        key: 'deliveredTimes',
        width: 150,
        align: 'center',
        hideInTable: !roles.includes('BGA_CT'),
        hideInSetting: !roles.includes('BGA_CT'),
        onCell: () => ({ className: `!p-0 !pt-0 divide-y` }),
        render: (_, record) => {
          const times = record?.deliveredTimes || [];
          return times.length ? times.map((time, index) => (
            <div key={index} className={`w-full py-1 px-2`}>
              {moment(time).format("DD/MM/YYYY HH:mm")}
            </div>
          )) : '-'
        },
        ...getColumnSearchProps('deliveredTimes', 'arrayDate')
      },
      {
        title: t('waste-handover-plan-register.product'),
        dataIndex: 'listProduct',
        key: 'listProduct',
        width: 350,
        onCell: () => ({ className: `!p-0 !pt-0 divide-y` }),
        render: (_, record) => {
          return record?.listProduct.length ? record.listProduct.map((prd, index) => (
            <div key={index} className={`w-full py-1 px-2 truncate`}>
              {prd}
            </div>
          )) : '-'
        },
        ...getColumnSearchProps('listProduct', 'arrayString')
      },
      {
        title: t('waste-handover-plan-register.driver-info.name'),
        dataIndex: 'driverName',
        key: 'driverName',
        width: 250,
        ...getColumnSearchProps('driverName')
      },
      {
        title: t('waste-handover-plan-register.driver-info.phone'),
        dataIndex: 'phone',
        key: 'phone',
        width: 130,
        ...getColumnSearchProps('phone')
      },
      {
        title: t('waste-handover-plan-register.driver-info.license-plate'),
        dataIndex: 'licensePlate',
        key: 'licensePlate',
        width: 150,
        ...getColumnSearchProps('licensePlate')
      },
      {
        title: t('waste-handover-plan.creator'),
        dataIndex: 'createByName',
        key: 'createByName',
        width: 150,
        ...getColumnSearchProps('createByName')
      },
      {
        title: t('waste-handover-plan-register.actual-time-change-status'),
        dataIndex: 'lastEditTime',
        key: 'lastEditTime',
        width: 200,
        align: 'center',
        fieldProps: {
          format: 'DD/MM/YYYY HH:mm:ss',
        },
        valueType: 'dateTime',
        ...getColumnSearchProps('lastEditTime', 'date')
      },
      {
        title: t('masterdata.status'),
        dataIndex: 'actived',
        width: 140,
        align: 'center',
        key: 'actived',
        render: (text, record) => {
          if (record.actived === true) {
            return <CheckStatus className="!fill-[#389e0d] mx-auto" width="15" />
          } else if (record.actived === false) {
            return <CloseStatus width="15" className="!fill-red-500 mx-auto" />
          }
          return text
        }
      },
      {
        title: t('waste-handover-plan-register.cancel-reason-title'),
        dataIndex: 'cancelReason',
        key: 'cancelReason',
        width: 200,
        render: (text, record) => record.cancelReason || '-',
      },
      {
        title: t('masterdata.function'),
        key: 'functions',
        width: 90,
        align: 'center',
        hideInTable: !isCancel,
        hideInSetting: !isCancel,
        render: (_, record) => {
          return (
            <div className="w-full flex items-center justify-center gap-x-1">
              {record.isShowCancelButton && (
                <Tooltip title={t('waste-handover-plan-register.cancel')}>
                  <CustomButtonIcon
                    color="#FC5C5E"
                    onClick={() => {
                      setDataCancel(record);
                      setModalCancelOpen(true);
                    }}
                  >
                    <Trash className="!fill-[#FC5C5E]" width="12" height="12" />
                  </CustomButtonIcon>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ]

    const [columns, setColumns] = useState<ProColumns<WasteHandoverRegistrationModel>[]>(defaultDataTable)

    const getDataTable = async (values: WasteHandoverRegistrationSearchPayload) => {
      setTableLoading(true)
      await wasteHandoverPlanPresenter.searchWasteHandoverRegistration(wasteHandoverPlanStore, { ...values, paging: { ...values.paging, pageSize: 1000000000 } })
      setTableLoading(false)
      if (wasteHandoverPlanStore.searchWasteHandoverRegistrationResponse?.isSuccess) {
        // Map dữ liệu từ API response, lấy deliveredTimes từ deliveryTime
        const mappedData = wasteHandoverPlanStore.searchWasteHandoverRegistrationResponse?.data.map((item: any) => {
          // Tạo deliveredTimes từ deliveryTime (string) hoặc deliveredTimes (array) nếu có
          let deliveredTimes: string[] = [];
          if (item.deliveryTime) {
            deliveredTimes = [item.deliveryTime];
          } else if (item.deliveredTimes && Array.isArray(item.deliveredTimes)) {
            deliveredTimes = item.deliveredTimes;
          }

          return {
            ...item,
            deliveredTimes: deliveredTimes
          };
        });
        setDataTable(mappedData)
        return mappedData
      }
      return []
    }



    // lấy common date
    const getListCommom = async () => {
      if (!commonStore.listCommonDate2.length) {
        await commonPresenter.getListCommonDate(commonStore, 'CommonDate2')
      }
    }
    // Dropdown nhà cung cấp
    const getDropdownVendor = async (keyword?: string) => {
      await commonPresenter.getDropdownVendor(commonStore, { keyword: keyword, type: 'NCC_CT' })
      if (commonStore.commonRespone.isSuccess && commonStore.commonRespone.data.length) {
        setListVendor(commonStore.commonRespone.data)
      }
    }
    const searchVendor = useCallback(debounce(getDropdownVendor, 300), [])

    // set lại colum khi data table thay đổi
    useEffect(() => {
      setColumns(prev => {
        const col = defaultDataTable.map((column, index) => ({ ...column, width: prev[index]?.width || column.width }))
        return col
      })
    }, [dataTable, searchText]);

    useEffect(() => {
      const initLoadData = async () => {
        // Kiểm tra xem có saved search params không
        const savedParams = getSearchParams('wasteHandoverRegistration');

        if (savedParams) {
          // Nếu có saved params, load lại trạng thái search trước đó
          console.log('Loading saved search params:', savedParams);

          let paramsToUse = { ...savedParams };

          // Nếu có common preset (khác 'Custom'), gọi lại API để lấy ngày hiện tại cho preset đó
          // Điều này đảm bảo "Today" luôn là hôm nay, "ThisMonth" là tháng này, v.v.
          if (savedParams.common && savedParams.common !== 'Custom') {
            await commonPresenter.getDateByCommonDate(commonStore, savedParams.common);
            if (commonStore.commonDate.fromDate) {
              paramsToUse = {
                ...paramsToUse,
                documentDateFrom: commonStore.commonDate.fromDate,
                documentDateTo: commonStore.commonDate.toDate,
              };
              // Lưu lại params với ngày đã cập nhật
              saveSearchParams('wasteHandoverRegistration', paramsToUse);
            }
          }

          // Convert string dates back to dayjs để hiển thị trong form
          const paramsWithDates = {
            ...paramsToUse,
            documentDateFrom: paramsToUse.documentDateFrom ? dayjs(paramsToUse.documentDateFrom) : undefined,
            documentDateTo: paramsToUse.documentDateTo ? dayjs(paramsToUse.documentDateTo) : undefined,
          };

          setDataPaging(paramsToUse);

          // Set form values từ saved params - bao gồm cả common date preset
          searchForm.setFieldsValue({
            ...omit(paramsWithDates, ['paging']),
            common: paramsToUse.common || 'Today',
          });

          // Set disable state cho date picker
          setDisableDatePicker(paramsToUse.common && paramsToUse.common !== 'Custom');

          getDataTable(paramsToUse);
        } else {
          // Nếu không có saved params, load data với giá trị mặc định
          getDataTable(dataPaging);
        }
      };

      initLoadData();
      getDropdownVendor();
      getListCommom();
      commonPresenter.getDropdownWasteMaterialTaskStatus(commonStore);
      tableRef.current?.reload();
    }, []);

    const vendor_number = getUserName();
    const vendor_name = getFullName();
    const role = getRole();

    // If the role is "NCC", set the initial value for the vendorNumber
    const initialValues = {
      vendorNumber: role === 'NCC_CT' ? vendor_number : '',
    };

    useEffect(() => {
      // console.log('roleeeeeeeeeee', role);
      if (role === 'NCC_CT' && vendor_number) {
        // Check if the vendorNumber exists in the options
        const valueExists = listVendor.some(option => option.key === vendor_number);

        if (!valueExists && vendor_name) {
          // Add the vendorNumber and its label to the options array
          setListVendor(prevOptions => [
            ...prevOptions,
            { key: vendor_number, value: vendor_number + " | " + vendor_name }
          ]);
        }

        // Set the initial value for the form
        searchForm.setFieldsValue({ vendorNumber: vendor_number });
      }
    }, [vendor_number, vendor_name, role, listVendor, searchForm]);

    // == CẤU HÌNH TABLE =================================================================
    const tableRef = useRef<ActionType>();
    const constDataName = 'DanhSachWasteHandoverRegistration';
    const {
      isResizablePresent,
      setIsResizablePresent,
      resizableColumnSelect,
      columnsState,
      handleColumnsStateChange,
      templateReportModalRef,
      userReports,
      selectedReportId,
      templateReportModalProps,
      handleResize,
    } = useResizableColumns<WasteHandoverRegistrationModel>(columns, setColumns, constDataName);

    const [isLoading, setIsLoading] = useState(false);

    const isExport = checkPermissionWithId('EXPORT')


    const handleResetFilters = async () => {
      searchForm.resetFields();
      initialSearch.paging.pageIndex = 1;
      setDataPaging(initialSearch);
      setTableKey(prev => prev + 1);
      setSearchText({});
      searchForm.submit();
    };

    return (
      <PageWrapper breadcrumb={[{
        text: t('waste-handover-plan-register.title')
      }]}
        extras={
          <>
            {isExport && <ExportButton loading={isLoading} onClick={async () => {
              setIsLoading(true);
              // TODO: Implement export
              setIsLoading(false);
            }} />}
            <CustomButton
              onClick={() => setOpenDrawer(true)}
              type="primary" color="#EFF4F9"
              className="!px-0 w-8"
            >
              <Filter className="!fill-primary" />
            </CustomButton>
          </>
        }
      >
        {/* Table */}
        < BoxContainer >
          <CustomProTable<WasteHandoverRegistrationModel>
            loading={tableLoading}
            bordered
            columns={columns}
            key={tableKey}
            dataSource={dataTable}
            actionRef={tableRef}
            handleResize={handleResize}
            isResizablePresent={isResizablePresent}
            resizableColumnSelect={resizableColumnSelect}
            columnsState={{
              value: Object.keys(columnsState).length > 0 ? columnsState : undefined,
              onChange: (newColumnsState) => {
                handleColumnsStateChange(newColumnsState);
              },
            }}
            toolBarRender={() => [
              <SaveReportForm
                setIsResizablePresent={setIsResizablePresent}
                templateReportModalRef={templateReportModalRef}
                userReports={userReports}
                selectedReportId={selectedReportId}
                templateReportModalProps={templateReportModalProps}
              />,
            ]}
            rowKey="stt"
            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              position: ['topRight'],
            }}

          />
        </BoxContainer>
        <SearchBox
          onClose={() => { setOpenDrawer(false) }}
          className="lg:!w-[50%]"
          open={openDrawer}
          footer={
            <div className='flex gap-2 justify-end'>
              <CustomButton
                onClick={() => {
                  handleResetFilters();
                  setOpenDrawer(false);
                }}
              >
                {'Reset tìm kiếm'}
              </CustomButton>
              <CustomButton
                htmlType="submit"
                // className="my-auto ml-auto"
                type="primary"
                icon={<HiSearch />}
                onClick={() => {
                  searchForm.submit();
                }}
              >
                Tìm kiếm
              </CustomButton>
            </div>
          }
        >
          <SearchForm
            form={searchForm}
            initialValues={initialValues}
            className=" !gap-y-4 !pb-24"
            onFinish={(values) => {
              const valueClone = cloneDeep(values)
              // convert lại from date to date
              const documentDateFrom = valueClone?.documentDateFrom ? valueClone.documentDateFrom?.format("YYYY-MM-DD") : undefined
              const documentDateTo = valueClone?.documentDateTo ? valueClone.documentDateTo?.format("YYYY-MM-DD") : undefined
              Object.assign(valueClone, { documentDateFrom, documentDateTo })
              // bỏ các giá trị rỗng null và trim giá trị
              removeEmptyValues(valueClone)
              const dataSearch: WasteHandoverRegistrationSearchPayload = { paging: { ...dataPaging.paging, pageIndex: 1 }, ...valueClone }
              setDataPaging(dataSearch)

              // Lưu search params vào localStorage
              saveSearchParams('wasteHandoverRegistration', dataSearch)

              setOpenDrawer(false)
              setTableKey(prev => prev + 1)
              setSearchText({})
              getDataTable(dataSearch)
            }}
          >
            {/* Nhà cung cấp */}
            <SearchItem name={'vendorNumber'} label={t('waste-handover-plan.vendor')}>
              <Select
                allowClear
                options={listVendor}
                fieldNames={{ label: "value", value: 'key' }}
                filterOption={false}
                popupMatchSelectWidth={false}
                disabled={role === 'NCC_CT' ? true : false}
                placeholder={t('place-holder.please-select')}
                showSearch onSearch={(values) => searchVendor(values || undefined)}
              />
            </SearchItem>
            {/* Mã phiếu đăng ký */}
            <SearchItem name={'wasteHandoverRegistrationCode'} label={t('waste-handover-plan-register.title')}>
              <Input placeholder={t('place-holder.type-content')} />
            </SearchItem>
            {/* Mã chất thải/ phế liệu */}
            <SearchItem name={'productCode'} label={t('masterdata.waste-product.product-code')}>
              <Input placeholder={t('place-holder.enter-waste-code')} />
            </SearchItem>
            {/* Tên chất thải/ phế liệu */}
            <SearchItem name={'productName'} label={t('masterdata.waste-product.product-name')}>
              <Input placeholder={t('place-holder.enter-waste-name')} />
            </SearchItem>
            {/* Tên tài xế */}
            <SearchItem name={'driverName'} label={t('waste-handover-plan-register.driver-info.name')}>
              <Input placeholder={t('waste-handover-plan-register.driver-info.name')} />
            </SearchItem>
            {/* tình thái phiếu */}
            <SearchItem name={'taskStatusCode'} label={t('waste-handover-plan-register.status')}>
              <Select
                allowClear
                options={commonStore.dropdownWasteMaterialTaskStatus}
                fieldNames={{ label: "value", value: 'key' }}
                placeholder={t('place-holder.please-select')}
              />
            </SearchItem>

            {/* Ngày giao hàng */}
            <SearchItem initialValue={'Today'} name={'common'} label={t('waste-handover-plan.delivery-date-preset')} start>
              <Select options={commonStore.listCommonDate2} fieldNames={{ label: 'value', value: 'key' }} onChange={async (value) => {
                if (value && value !== "Custom") {
                  setDisableDatePicker(true)
                  await commonPresenter.getDateByCommonDate(commonStore, value)
                  if (commonStore.commonDate.fromDate) {
                    searchForm.setFieldsValue({ documentDateFrom: dayjs(commonStore.commonDate.fromDate, "YYYY-MM-DD"), documentDateTo: dayjs(commonStore.commonDate.toDate, "YYYY-MM-DD") })
                  }
                } else {
                  setDisableDatePicker(false)
                  searchForm.setFieldsValue({ documentDateFrom: undefined, documentDateTo: undefined })
                }
              }} />
            </SearchItem>

            {/* Trạng thái kích hoạt (actived) */}
            <SearchItem name={'actived'} label={t('masterdata.status')}>
              <Select
                allowClear
                placeholder={t('place-holder.please-select')}
                options={[
                  { label: t('radio-btn.active'), value: true },
                  { label: t('radio-btn.in-active'), value: false },
                ]}
              />
            </SearchItem>
            {/* Từ ngày */}
            <SearchItem
              initialValue={dayjs()}
              name={'documentDateFrom'} label={t('waste-handover-plan.from-date')}
              start
              dependencies={['documentDateTo']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value || getFieldValue('documentDateTo')) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t('message.required-input')));
                  },
                })
              ]}
            >
              <DatePicker changeOnBlur disabled={disableDatePicker} format={DATE_FORMAT_DEFAULT} className="w-full" />
            </SearchItem>
            {/* Đến ngày */}
            <SearchItem
              initialValue={dayjs()}
              name={'documentDateTo'}
              label={t('waste-handover-plan.to-date')}
              dependencies={['documentDateFrom']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value || getFieldValue('documentDateFrom')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('message.required-input')));
                  },
                })
              ]}
            >
              <DatePicker changeOnBlur disabled={disableDatePicker} format={DATE_FORMAT_DEFAULT} className="w-full" />
            </SearchItem>
          </SearchForm>
        </SearchBox>

        <CancelConfirm
          open={modalCancelOpen}
          onOpenChange={(open) => {
            setModalCancelOpen(open);
            setDataCancel(undefined);
          }}
          loading={cancelLoading}
          dataCancel={dataCancel}
          onFormSubmit={(value) => {
            value.then(async (data) => {
              setCancelLoading(true);
              const result = await wasteHandoverPlanPresenter.cancelWasteHandoverRegistration(data);
              setCancelLoading(false);
              if (result) {
                setDataCancel(undefined);
                setModalCancelOpen(false);
                tableRef.current?.reload();
                getDataTable(dataPaging);
              }
            }).catch((error) => {
              setCancelLoading(false);
            });
          }}
        />
      </PageWrapper >

    )
  })
}

